import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { sendOrderConfirmationEmail } from "@/utils/order-email.functions";
import { getStripeEnvironment } from "@/lib/stripe";

export const Route = createFileRoute("/checkout/return")({
  head: () => ({
    meta: [
      { title: "Order Confirmed · SOXIE365" },
      { name: "description", content: "Your SOXIE365 365 Pack order is confirmed. One year of daily fresh socks, on its way." },
      { property: "og:title", content: "Order Confirmed · SOXIE365" },
      { property: "og:description", content: "Your SOXIE365 365 Pack order is confirmed. One year of daily fresh socks, on its way." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: CheckoutReturn,
});

function CheckoutReturn() {
  const { session_id: sessionId } = Route.useSearch();
  const sendConfirmation = useServerFn(sendOrderConfirmationEmail);

  // Order placed — empty the local cart so the nav badge resets.
  useEffect(() => {
    if (!sessionId) return;
    try {
      localStorage.removeItem("soxie365_cart_v2");
    } catch {}
  }, [sessionId]);

  // Fire the branded order-confirmation email (idempotent server-side).
  useEffect(() => {
    if (!sessionId) return;
    void sendConfirmation({
      data: { sessionId, environment: getStripeEnvironment() },
    }).catch(() => {});
  }, [sessionId, sendConfirmation]);

  return (
    <main className="min-h-screen bg-white text-black flex items-center justify-center px-6">
      <div className="max-w-lg w-full border-2 border-black p-10">
        <p className="font-mono text-xs uppercase tracking-widest text-black/50">[ SOXIE365 ]</p>
        <h1 className="mt-4 text-4xl font-extrabold uppercase tracking-tighter leading-none">
          {sessionId ? "Order confirmed" : "No order found"}
        </h1>
        <p className="mt-4 text-black/60">
          {sessionId
            ? "Charged in EUR. Your 365 Pack is being prepared — one year of daily fresh socks, one delivery."
            : "We couldn't find an order reference. If you were charged, check your email for the receipt."}
        </p>
        {sessionId && (
          <p className="mt-4 font-mono text-[11px] break-all text-black/40">REF: {sessionId}</p>
        )}
        <Link
          to="/"
          className="mt-8 inline-block bg-black text-white px-6 py-3 font-bold uppercase tracking-tighter hover:bg-accent hover:text-black transition-colors"
        >
          Back to SOXIE365
        </Link>
      </div>
    </main>
  );
}